import Segmented from '@/components/base/Segmented';
import Toggle from '@/components/base/Toggle';
import type { Printer, PrinterRole, PrinterSettings } from '@/services/print/printTypes';

interface PrinterConfigCardProps {
  role: PrinterRole;
  title: string;
  icon: string;
  settings: PrinterSettings;
  runtime: 'WEB' | 'ELECTRON';
  printers: Printer[];
  disabled?: boolean;
  onChange: (patch: Partial<PrinterSettings>) => void;
  onTest: () => void;
}

const WIDTH_OPTIONS = [
  { value: '58mm', label: '58 mm' },
  { value: '80mm', label: '80 mm' },
  { value: 'A4', label: 'A4' },
];

const TYPE_OPTIONS = [
  { value: 'thermal', label: 'Térmica' },
  { value: 'standard', label: 'Estándar' },
];

const COPIES_OPTIONS = [
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
];

export default function PrinterConfigCard({
  role,
  title,
  icon,
  settings,
  runtime,
  printers,
  disabled,
  onChange,
  onTest,
}: PrinterConfigCardProps) {
  const isReceipt = role === 'receipt';
  const isLabel = role === 'label';

  return (
    <section
      className={`rounded-lg border border-background-200 bg-background-50 p-5 md:p-6 ${
        disabled ? 'opacity-60' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center shrink-0">
            <i className={`${icon} text-lg`} />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-foreground-950">{title}</h3>
            {isLabel && (
              <span className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-foreground-500">
                <i className="ri-time-line" /> Próximamente
              </span>
            )}
          </div>
        </div>

        {isReceipt && (
          <div className="flex items-center gap-2 text-sm text-foreground-700">
            <span>Principal</span>
            <Toggle
              checked={settings.is_default}
              onChange={(v) => onChange({ is_default: v })}
              label="Usar como impresora principal"
            />
          </div>
        )}
      </div>

      <div className="mt-5 space-y-5">
        {/* Selección de impresora */}
        <div>
          <label className="block text-sm font-medium text-foreground-800 mb-1.5">
            Impresora seleccionada
          </label>
          {runtime === 'ELECTRON' ? (
            <select
              value={settings.printer_id ?? ''}
              onChange={(e) => {
                const id = e.target.value;
                const printer = printers.find((p) => p.id === id);
                onChange({
                  printer_id: id || null,
                  printer_name: printer ? printer.name : null,
                });
              }}
              className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
            >
              <option value="">— Sin impresora seleccionada —</option>
              {printers.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              value={settings.printer_name ?? ''}
              onChange={(e) => onChange({ printer_name: e.target.value })}
              placeholder="Se usa el diálogo del navegador"
              className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          )}
          <p className="mt-1 text-xs text-foreground-500">
            {runtime === 'ELECTRON'
              ? 'Lista de impresoras detectadas en este equipo.'
              : 'Las impresiones desde navegador utilizan el sistema de impresión del dispositivo.'}
          </p>
        </div>

        {/* Tipo */}
        <div>
          <p className="text-sm font-medium text-foreground-800 mb-1.5">Tipo</p>
          <Segmented
            options={TYPE_OPTIONS}
            value={settings.printer_type}
            onChange={(v) => onChange({ printer_type: v as PrinterSettings['printer_type'] })}
          />
        </div>

        {/* Ancho de papel */}
        <div>
          <p className="text-sm font-medium text-foreground-800 mb-1.5">Ancho de papel</p>
          <Segmented
            options={WIDTH_OPTIONS}
            value={settings.paper_width}
            onChange={(v) => onChange({ paper_width: v as PrinterSettings['paper_width'] })}
          />
        </div>

        {/* Copias */}
        <div>
          <p className="text-sm font-medium text-foreground-800 mb-1.5">Número de copias</p>
          <Segmented
            options={COPIES_OPTIONS}
            value={String(settings.copies)}
            onChange={(v) => onChange({ copies: Number(v) })}
          />
        </div>

        {/* Opciones propias de factura */}
        {isReceipt && (
          <>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-foreground-900">Corte automático</p>
                <p className="mt-0.5 text-xs text-foreground-500">
                  Solo se ejecuta si la impresora y el protocolo lo permiten.
                </p>
              </div>
              <Toggle
                checked={settings.auto_cut}
                onChange={(v) => onChange({ auto_cut: v })}
                label="Corte automático"
              />
            </div>

            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-foreground-900">Abrir gaveta después de cobrar</p>
                <p className="mt-0.5 text-xs text-foreground-500">
                  {runtime === 'ELECTRON'
                    ? 'Se comunica con la gaveta desde el hardware compatible.'
                    : 'Requiere conexión mediante Electron.'}
                </p>
              </div>
              <Toggle
                checked={settings.open_drawer}
                onChange={(v) => onChange({ open_drawer: v })}
                label="Abrir gaveta"
              />
            </div>

            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-foreground-900">
                  Imprimir factura automáticamente después de cobrar
                </p>
                <p className="mt-0.5 text-xs text-foreground-500">
                  La factura se imprime después de guardar la venta correctamente.
                </p>
              </div>
              <Toggle
                checked={settings.auto_print}
                onChange={(v) => onChange({ auto_print: v })}
                label="Impresión automática"
              />
            </div>
          </>
        )}

        {/* Impresión de prueba */}
        <div className="pt-2 border-t border-background-200">
          <button
            type="button"
            onClick={onTest}
            disabled={disabled}
            className="inline-flex items-center gap-2 rounded-md border border-background-300 px-3 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition disabled:opacity-50 cursor-pointer"
          >
            <i className="ri-printer-line" /> Imprimir prueba
          </button>
        </div>
      </div>
    </section>
  );
}